/**
 * OPEN-156 — void an approved ticket that is not on a live invoice.
 *
 * Inline, like the invoice void (DEC-322), because it needs a reason and nothing undoes it. The
 * copy says plainly what happens and that it is final (coordinator session 6). The API enforces
 * VOID_BILLING_TICKET, the reason rule and the invoice check; this is the form.
 */

import React from 'react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { useVoidBillingTicket } from './hooks';

/** The same bar as the API (TICKET_VOID_REASON_MIN) and DEC-355's Void load. */
export const VOID_TICKET_REASON_MIN = 10;

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  color: 'var(--color-text-secondary)',
  marginBottom: '4px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
  resize: 'vertical',
};

export function VoidTicketPanel({
  jobId,
  onClose,
}: {
  jobId: string;
  onClose: () => void;
}): React.JSX.Element {
  const [reason, setReason] = React.useState('');
  const voidTicket = useVoidBillingTicket(jobId, () => {
    toast.success('Ticket voided. Its charges were removed from billing.');
    onClose();
  });
  const reasonOk = reason.trim().length >= VOID_TICKET_REASON_MIN;
  const busy = voidTicket.isPending;

  return (
    <div
      data-testid="void-ticket-panel"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        maxWidth: '480px',
        padding: '12px',
        marginBottom: '20px',
        borderRadius: 'var(--radius-card)',
        backgroundColor: 'var(--color-bg-secondary)',
      }}
    >
      <p style={{ margin: 0, fontSize: '14px' }}>
        Voiding cancels this ticket and removes its charges from billing. It will not be invoiced.
        <strong> This can’t be undone.</strong>
      </p>
      <div>
        <label style={labelStyle} htmlFor="void-ticket-reason">
          Reason for voiding (at least {VOID_TICKET_REASON_MIN} characters)
        </label>
        <textarea
          id="void-ticket-reason"
          rows={3}
          maxLength={500}
          value={reason}
          onChange={(e): void => setReason(e.target.value)}
          style={inputStyle}
        />
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="destructive"
          disabled={busy || !reasonOk}
          onClick={(): void => {
            voidTicket.mutate({ reason: reason.trim() });
          }}
        >
          {busy ? 'Voiding…' : 'Void ticket'}
        </Button>
      </div>
      {voidTicket.isError && (
        <span role="alert" style={{ fontSize: '12px', color: 'var(--color-danger)' }}>
          The void was refused. Reload the ticket: it may now be on an invoice.
        </span>
      )}
    </div>
  );
}
